import { t } from "@/lib/i18n/interpolate";
import type {
  DocBlock,
  DocGridCell,
  DocSection,
  DocumentModel,
} from "@/lib/tool-kit/document";
import { fieldOrPrompt, splitItems } from "@/lib/tool-kit/essay-templates/text";

/**
 * A SWOT analysis as the familiar 2×2 grid — strengths and weaknesses (internal) above
 * opportunities and threats (external) — followed by TOWS strategy prompts (Weihrich,
 * 1982), which pair the top items of two quadrants into questions: how could this
 * strength help with that opportunity, what if this threat lands on that weakness.
 */

export const SWOT_QUADRANTS = [
  "strengths",
  "weaknesses",
  "opportunities",
  "threats",
] as const;

export type SwotQuadrant = (typeof SWOT_QUADRANTS)[number];

export const TOWS_STRATEGIES = ["so", "wo", "st", "wt"] as const;

export type TowsStrategy = (typeof TOWS_STRATEGIES)[number];

/** Which two quadrants each strategy pairs, in the order its question names them. */
const TOWS_PAIRS: Record<TowsStrategy, [SwotQuadrant, SwotQuadrant]> = {
  so: ["strengths", "opportunities"],
  wo: ["weaknesses", "opportunities"],
  st: ["strengths", "threats"],
  wt: ["weaknesses", "threats"],
};

export type SwotInput = Record<SwotQuadrant, string> & {
  subject: string;
  objective: string;
  tows: boolean;
};

export interface SwotLabels {
  defaultTitle: string;
  titleWithSubject: string;
  fields: { objective: string };
  objectivePrompt: string;
  rankNote: string;
  quadrants: Record<SwotQuadrant, string>;
  captions: Record<SwotQuadrant, string>;
  quadrantPrompts: Record<SwotQuadrant, string>;
  towsHeading: string;
  towsNote: string;
  strategies: Record<TowsStrategy, string>;
  pairQuestions: Record<TowsStrategy, string>;
  missingPair: string;
  strategyPrompt: string;
}

/** How many items from the top of each list are paired — the ones ranked first. */
export const TOWS_TOP_ITEMS = 2;

/** Every pairing of the first `limit` items of each list, first item first. */
export function towsPairs(
  first: readonly string[],
  second: readonly string[],
  limit = TOWS_TOP_ITEMS
): [string, string][] {
  const pairs: [string, string][] = [];
  for (const a of first.slice(0, limit)) {
    for (const b of second.slice(0, limit)) {
      pairs.push([a, b]);
    }
  }
  return pairs;
}

function strategyBlocks(
  strategy: TowsStrategy,
  lists: Record<SwotQuadrant, string[]>,
  labels: SwotLabels
): DocBlock[] {
  const [firstQuadrant, secondQuadrant] = TOWS_PAIRS[strategy];
  const pairs = towsPairs(lists[firstQuadrant], lists[secondQuadrant]);
  const questions: DocBlock =
    pairs.length > 0
      ? {
          kind: "list",
          list: {
            style: "bullet",
            items: pairs.map(([a, b]) => ({
              text: t(labels.pairQuestions[strategy], { a, b }),
            })),
          },
        }
      : {
          kind: "guidance",
          text: t(labels.missingPair, {
            first: labels.quadrants[firstQuadrant],
            second: labels.quadrants[secondQuadrant],
          }),
        };
  return [
    { kind: "subheading", text: labels.strategies[strategy] },
    questions,
    { kind: "prompt", text: labels.strategyPrompt },
  ];
}

export function buildSwot(input: SwotInput, labels: SwotLabels): DocumentModel {
  const subject = input.subject.trim();
  const lists = Object.fromEntries(
    SWOT_QUADRANTS.map((quadrant) => [quadrant, splitItems(input[quadrant])])
  ) as Record<SwotQuadrant, string[]>;
  const cells: DocGridCell[] = SWOT_QUADRANTS.map((quadrant) => ({
    title: labels.quadrants[quadrant],
    caption: labels.captions[quadrant],
    items: lists[quadrant],
    prompt: labels.quadrantPrompts[quadrant],
  }));
  const tows: DocSection[] = input.tows
    ? [
        {
          heading: labels.towsHeading,
          blocks: [
            { kind: "guidance", text: labels.towsNote },
            ...TOWS_STRATEGIES.flatMap((strategy) =>
              strategyBlocks(strategy, lists, labels)
            ),
          ],
        },
      ]
    : [];
  return {
    title: subject
      ? t(labels.titleWithSubject, { subject })
      : labels.defaultTitle,
    sections: [
      {
        blocks: [
          fieldOrPrompt(
            labels.fields.objective,
            input.objective,
            labels.objectivePrompt
          ),
          { kind: "grid", cells },
          { kind: "guidance", text: labels.rankNote },
        ],
      },
      ...tows,
    ],
  };
}
